import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const imgUrl = (src) => {
  if (!src) return "";
  if (src.startsWith("http") || src.startsWith("data:")) return src;
  // Locally-stored uploads are returned as /api/uploads/... — prefix with backend base URL
  return `${BACKEND}${src}`;
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch { return ""; }
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/blog/${slug}`).then((r) => {
      setPost(r.data);
      // Update document title for SEO
      if (r.data?.title) document.title = `${r.data.title} | Lone Star Speed`;
      if (r.data?.meta_description) {
        let meta = document.querySelector('meta[name="description"]');
        if (!meta) {
          meta = document.createElement("meta");
          meta.name = "description";
          document.head.appendChild(meta);
        }
        meta.content = r.data.meta_description;
      }
      // Inject FAQ structured data for SEO
      if (Array.isArray(r.data?.faq_schema) && r.data.faq_schema.length > 0) {
        const json = {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: r.data.faq_schema.map((q) => ({
            "@type": "Question",
            name: q.question,
            acceptedAnswer: { "@type": "Answer", text: q.answer },
          })),
        };
        let script = document.getElementById("blog-faq-jsonld");
        if (!script) {
          script = document.createElement("script");
          script.type = "application/ld+json";
          script.id = "blog-faq-jsonld";
          document.head.appendChild(script);
        }
        script.text = JSON.stringify(json);
      }
    }).catch(() => setNotFound(true));
    return () => {
      document.title = "Lone Star Speed";
      const s = document.getElementById("blog-faq-jsonld");
      if (s) s.remove();
    };
  }, [slug]);

  if (notFound) {
    return (
      <div className="bg-ink text-white min-h-screen pt-32 pb-24" data-testid="blog-not-found">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <div className="text-mono text-[11px] uppercase tracking-[0.4em] text-adrenaline">404</div>
          <h1 className="text-display text-5xl uppercase mt-3">Article Not Found</h1>
          <Link to="/blog" className="text-mono text-xs uppercase tracking-[0.3em] mt-8 inline-block border border-white/20 px-6 py-3 hover:border-white transition-colors">← Back to Blog</Link>
        </div>
      </div>
    );
  }

  if (!post) {
    return <div className="bg-ink text-white min-h-screen pt-32 px-6 text-zinc-500 text-mono text-xs uppercase tracking-widest">Loading…</div>;
  }

  return (
    <article className="bg-ink text-white min-h-screen pt-32 pb-24" data-testid="blog-post">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <Link to="/blog" className="text-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500 hover:text-adrenaline transition-colors" data-testid="blog-back">← The Journal</Link>
        <div className="flex flex-wrap gap-2 mt-8">
          {(post.tags || []).slice(0, 4).map((t) => (
            <span key={t} className="text-mono text-[10px] uppercase tracking-[0.3em] text-adrenaline">{t}</span>
          ))}
        </div>
        <h1 className="text-display text-5xl md:text-7xl uppercase leading-[0.95] mt-4">{post.title}</h1>
        <div className="text-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500 mt-6">{formatDate(post.published_at)} · {post.author}</div>
        {post.hero_image && (
          <div className="mt-10 -mx-4 sm:mx-0 bg-black">
            <img src={imgUrl(post.hero_image)} alt={post.hero_image_alt || post.title} className="w-full h-auto" />
          </div>
        )}
        <div
          className="blog-content mt-12 text-zinc-200 leading-relaxed text-lg"
          dangerouslySetInnerHTML={{ __html: post.content_html || "" }}
          data-testid="blog-content"
        />
        {post.infographic_image && (
          <figure className="mt-12 bg-black border border-white/5">
            <img src={imgUrl(post.infographic_image)} alt={`${post.title} infographic`} className="w-full h-auto" />
            <figcaption className="text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 p-4 text-center">Infographic</figcaption>
          </figure>
        )}
        {Array.isArray(post.faq_schema) && post.faq_schema.length > 0 && (
          <section className="mt-16" data-testid="blog-faq">
            <h2 className="text-display text-3xl uppercase mb-6">Frequently Asked</h2>
            <div className="space-y-4">
              {post.faq_schema.map((q, idx) => (
                <details key={idx} className="bg-carbon border border-white/5 group">
                  <summary className="cursor-pointer p-5 text-heading uppercase tracking-wider text-white list-none flex justify-between items-center">
                    <span>{q.question}</span>
                    <span className="text-adrenaline group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <div className="px-5 pb-5 text-zinc-300 leading-relaxed">{q.answer}</div>
                </details>
              ))}
            </div>
          </section>
        )}

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-wrap justify-between gap-4 items-center">
          <div>
            <div className="text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Want To Build Yours?</div>
            <div className="text-display text-2xl uppercase mt-1">Custom Wheels Start At $2,749</div>
          </div>
          <Link to="/mesh-series" className="bg-adrenaline text-black text-mono text-xs uppercase tracking-[0.3em] px-6 py-3 hover:bg-adrenaline/90 transition-colors" data-testid="blog-mesh-cta">
            Build Your Set →
          </Link>
        </div>
      </div>
    </article>
  );
}
